import React from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { FormFields } from '../../AccountForm.meta';
import { isConsumerArea as selectIsClientArea } from '../../../../../../../../CommonSelectors/CommonAreaSelector';
import { ShowAccountToggle } from './components/ShowAccountToggle/ShowAccountToggle';
import { SyncAccountValueToggle } from './components/SyncAccountValueToggle/SyncAccountValueToggle';
import { SyncAssetAllocationToggle } from './components/SyncAssetAllocationToggle/SyncAssetAllocationToggle';
import { SyncHoldingsToggle } from './components/SyncHoldingsToggle/SyncHoldingsToggle';
import { SyncMonthlyContributionToggle } from './components/SyncMonthlyContributionToggle/SyncMonthlyContributionToggle';
import { SyncCostBasisToggle } from './components/SyncCostBasisToggle/SyncCostBasisToggle';
import { SyncTransactionsToggle } from './components/SyncTransactionsToggle/SyncTransactionsToggle';
import { TabWrapper } from '../../AccountForm.styles';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { MoveAccount } from './components/MoveAccount/MoveAccount';
import { AccountType } from '../../../../../../api/useAccountsOptionsQuery';

interface Props {
  isInsurance?: boolean;
}

export const SyncOptions = ({ isInsurance }: Props) => {
  if (isInsurance) {
    return (
      <TabWrapper>
        <ShowAccountToggle isInsurance />
        <SyncAccountValueToggle isInsurance />
      </TabWrapper>
    );
  }

  const formBag = useFormContext<FormFields>();

  const isClientArea = useSelector(selectIsClientArea);

  const [
    isLinked,
    isIntegrated,
    hasAsset,
    hasPositions,
    showSyncMonthlyContribution,
    isMoveAllowed,
    accountType,
    syncSourceLabel,
  ] = formBag.watch([
    'bankAccount.IsLinked',
    'bankAccount.IsIntegrated',
    'bankAccount.HasAsset',
    'bankAccount.HasPositions',
    'bankAccount.ShowSyncMonthlyContributionsToggle',
    'bankAccount.IsMoveAllowed',
    'bankAccount.AccountType',
    'bankAccount.SyncSourceLabel',
  ]);

  if (!isLinked && !isIntegrated) {
    throw new Error('Sync Options tab should only be used for Linked or Integrated accounts');
  }

  return (
    <TabWrapper>
      <ShowAccountToggle />
      <SyncAccountValueToggle />
      {hasAsset && <SyncAssetAllocationToggle />}
      {hasAsset && hasPositions && <SyncHoldingsToggle />}

      {isIntegrated && (
        <>
          {showSyncMonthlyContribution && <SyncMonthlyContributionToggle />}

          {!isClientArea && (
            <>
              {isMoveAllowed && <MoveAccount />}

              {syncSourceLabel && (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Text fontWeight={500}>This account is imported from {syncSourceLabel}</Text>

                  <Button
                    variant="link"
                    rightIcon="far fa-external-link-square"
                    onClick={() => ADV.util.Router.go2('AdviserSettings', { page: 'MySettings' })}
                  >
                    Settings
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {isLinked && !isIntegrated && (
        <>
          {accountType === AccountType.Brokerage && <SyncCostBasisToggle />}

          <SyncTransactionsToggle />
        </>
      )}
    </TabWrapper>
  );
};
